import { useState, useEffect, useCallback } from 'react';
import { useIntl } from 'react-intl';
import { checkTrialEligibility } from 'src/api/askSalah';
import type { AskSalahTrialEligibility } from 'src/types/askSalah';
import { useAskSalahSubscription } from './useAskSalahSubscription';

/**
 * Hook to check if user is eligible for Ask Salah free trial
 * 
 * Eligibility Rules:
 * - Requires any active SAL125 subscription (free trial OR paid)
 * - Cannot have already used Ask Salah trial
 * - Cannot have active Ask Salah subscription
 * 
 * Returns:
 * - isEligible: Boolean
 * - reason: String explanation if not eligible
 * - checkEligibility: Function to re-check
 * - isLoading: Loading state
 * - error: Error state
 */
export const useAskSalahTrialEligibility = () => {
  const intl = useIntl();
  const { hasSAL125Subscription, hasAskSalahAccess } = useAskSalahSubscription();
  const [eligibility, setEligibility] = useState<AskSalahTrialEligibility | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const checkEligibilityStatus = useCallback(async () => {
    // Quick local checks before API call
    if (!hasSAL125Subscription) {
      setEligibility({
        eligible: false,
        reason: 'no_sal125_subscription',
      });
      return;
    }

    if (hasAskSalahAccess) {
      setEligibility({
        eligible: false,
        reason: 'has_paid_subscription',
      });
      return;
    }

    // Check with backend
    try {
      setIsLoading(true);
      setError(null);
      const data = await checkTrialEligibility();
      setEligibility(data);
    } catch (err: any) {
      setError(err.message || 'Failed to check eligibility');
      console.error('Error checking trial eligibility:', err);
    } finally {
      setIsLoading(false);
    }
  }, [hasSAL125Subscription, hasAskSalahAccess]);

  // Check eligibility on mount and when dependencies change
  useEffect(() => {
    checkEligibilityStatus();
  }, [checkEligibilityStatus]);

  // Helper to get user-friendly message
  const getEligibilityMessage = useCallback(() => {
    if (!eligibility) return '';

    if (eligibility.eligible) {
      return intl.formatMessage({ 
        id: 'askSalah.trial.eligible', 
        defaultMessage: 'You are eligible for AskSalah free trial!' 
      });
    }

    switch (eligibility.reason) {
      case 'no_sal125_subscription':
        return intl.formatMessage({ 
          id: 'askSalah.error.noSAL125', 
          defaultMessage: 'Please subscribe to SAL125 to access AskSalah.' 
        });
      case 'already_used':
        return intl.formatMessage({ 
          id: 'askSalah.error.trialUsed', 
          defaultMessage: 'You have already used your AskSalah free trial.' 
        });
      case 'has_paid_subscription':
        return intl.formatMessage({ 
          id: 'askSalah.error.hasActiveSub', 
          defaultMessage: 'You already have an active AskSalah subscription.' 
        });
      default:
        return intl.formatMessage({ 
          id: 'askSalah.trial.notEligible', 
          defaultMessage: 'Not eligible for free trial.' 
        });
    }
  }, [eligibility, intl]);

  return {
    isEligible: eligibility?.eligible || false,
    reason: eligibility?.reason,
    checkEligibility: checkEligibilityStatus,
    isLoading,
    error,
    message: getEligibilityMessage(),
  };
};


